import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import UserMenu from "@/components/UserMenu";
import NurseCalendar from "@/components/NurseCalendar";
import CollapsibleCard from "@/components/CollapsibleCard";
import { toast } from "sonner";
import { format, parseISO, isAfter, isSameDay } from "date-fns";
import { fr } from "date-fns/locale";
import { 
  Calendar as CalendarIcon,
  Clock,
  MapPin,
  User,
  Phone,
  Mail,
  Settings,
  AlertCircle,
  AlertTriangle,
  CreditCard,
  CheckCircle,
  XCircle,
  Loader2,
  Euro,
  Check,
  X,
  RefreshCw,
  FileText,
  Send,
  MessageSquare,
  CalendarDays,
  ExternalLink,
  Flag
} from "lucide-react";

const LOGO_URL = "/logo-infirmiers.png";

const TIME_SLOTS = [
  "07:00","07:30","08:00","08:30","09:00","09:30","10:00","10:30",
  "11:00","11:30","12:00","12:30","13:00","13:30","14:00","14:30",
  "15:00","15:30","16:00","16:30","17:00","17:30","18:00","18:30",
  "19:00","19:30","20:00"
];

const DURATION_OPTIONS = [
  { value: 30, label: "30 min" },
  { value: 60, label: "1 h" },
  { value: 90, label: "1 h 30" },
  { value: 120, label: "2 h" }
];

const formatDuration = (minutes) => {
  const m = parseInt(minutes, 10);
  if (!m || isNaN(m)) return null;
  const h = Math.floor(m / 60);
  const min = m % 60;
  if (h > 0 && min > 0) return `${h} h ${min}`;
  if (h > 0) return `${h} h`;
  return `${min} min`;
};

const NurseDashboard = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [responding, setResponding] = useState(null);
  const [showProposalForm, setShowProposalForm] = useState(null);
  const [proposalData, setProposalData] = useState({ date: null, time: "" });
  const [markingCompleted, setMarkingCompleted] = useState(null);
  const [acceptModal, setAcceptModal] = useState(null); // { requestId }
  const [acceptDuration, setAcceptDuration] = useState(30);
  const [cannotCompleteModal, setCannotCompleteModal] = useState(null);
  const [submittingCannot, setSubmittingCannot] = useState(false);
  const [activeTab, setActiveTab] = useState("current"); // current, history, disputes
  const [showCalendar, setShowCalendar] = useState(false);
  const [statusFilter, setStatusFilter] = useState(null); // Filter for clicked stats
  const [disputes, setDisputes] = useState([]);
  const [disputeResponses, setDisputeResponses] = useState({}); // { [dispute_id]: text }
  const [submittingDisputeResponse, setSubmittingDisputeResponse] = useState(null);
  
  // Ref for scrolling to requests section
  const requestsListRef = React.useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const profileRes = await fetch(`${API}/nurses/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (profileRes.ok) {
        const profileData = await profileRes.json();
        setProfile(profileData);
        
        if (profileData.onboarding_step < 2) {
          navigate("/nurse/onboarding");
          return;
        }
      }

      const requestsRes = await fetch(`${API}/care-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const requestsData = await requestsRes.json();
      setRequests(requestsData.requests || []);

      // Fetch disputes against this nurse
      const disputesRes = await fetch(`${API}/disputes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (disputesRes.ok) {
        const disputesData = await disputesRes.json();
        setDisputes(disputesData.disputes || []);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitDisputeResponse = async (disputeId) => {
    const text = (disputeResponses[disputeId] || "").trim();
    if (text.length < 20) {
      toast.error("Votre réponse doit contenir au moins 20 caractères");
      return;
    }
    setSubmittingDisputeResponse(disputeId);
    try {
      const response = await fetch(`${API}/disputes/${disputeId}/nurse-response`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ response: text })
      });
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Erreur lors de l'envoi de votre réponse");
      }
      toast.success("Réponse enregistrée — Notre équipe va l'examiner");
      setDisputeResponses(prev => ({ ...prev, [disputeId]: "" }));
      const disputesRes = await fetch(`${API}/disputes`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (disputesRes.ok) {
        const data = await disputesRes.json();
        setDisputes(data.disputes || []);
      }
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmittingDisputeResponse(null);
    }
  };


  const handleNurseRespond = async (requestId, action, extra = {}) => {
    setResponding(requestId);
    try {
      const response = await fetch(`${API}/care-requests/${requestId}/nurse-respond`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ action, ...extra })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Erreur");
      }

      const actionLabels = {
        accept: "Demande acceptée — en attente du paiement du patient",
        refuse: "Demande refusée",
        propose_alternative: "Créneau alternatif proposé"
      };
      toast.success(actionLabels[action]);
      setShowProposalForm(null);
      setAcceptModal(null);
      setProposalData({ date: null, time: "" });
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setResponding(null);
    }
  };

  const handleProposeAlternative = (requestId) => {
    if (!proposalData.date || !proposalData.time) {
      toast.error("Veuillez sélectionner une date et un horaire");
      return;
    }
    handleNurseRespond(requestId, "propose_alternative", {
      proposed_date: format(proposalData.date, "yyyy-MM-dd"),
      proposed_time: proposalData.time
    });
  };

  const handleMarkCompleted = async (requestId) => {
    if (!window.confirm("Confirmez-vous que cette intervention a été réalisée ?")) return;
    
    setMarkingCompleted(requestId);
    try {
      const response = await fetch(`${API}/care-requests/${requestId}/mark-completed`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || "Erreur");
      }

      toast.success("Intervention marquée comme réalisée — Le patient va être notifié");
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setMarkingCompleted(null);
    }
  };

  const handleCannotComplete = async (action) => {
    if (!cannotCompleteModal) return;
    const comment = (cannotCompleteModal.comment || "").trim();
    if (action === "dispute" && comment.length < 20) {
      toast.error("Merci d'expliquer le motif (20 caractères minimum)");
      return;
    }
    if (action === "refund" && !window.confirm("Confirmer le remboursement intégral du patient ? Cette action est définitive.")) return;

    setSubmittingCannot(true);
    try {
      const response = await fetch(`${API}/care-requests/${cannotCompleteModal.requestId}/nurse-cannot-complete`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ action, comment })
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.detail || "Erreur");
      toast.success(data.message || "Action effectuée");
      setCannotCompleteModal(null);
      fetchData();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setSubmittingCannot(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      matched: { class: "bg-amber-500/20 text-amber-400 border-amber-500/30", label: "Nouvelle demande" },
      pending_payment: { class: "bg-[#00E073]/20 text-[#00E073] border-[#00E073]/30", label: "En attente de paiement" },
      pending_nurse_validation: { class: "bg-amber-500/20 text-amber-400 border-amber-500/30", label: "En attente de validation" },
      confirmed: { class: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", label: "Confirmé" },
      completed_by_nurse: { class: "bg-blue-500/20 text-blue-400 border-blue-500/30", label: "En attente validation patient" },
      completed: { class: "bg-green-500/20 text-green-400 border-green-500/30", label: "Terminé et validé" },
      cancelled: { class: "bg-red-500/20 text-red-400 border-red-500/30", label: "Annulé" },
      refused: { class: "bg-red-500/20 text-red-400 border-red-500/30", label: "Refusé" },
      alternative_proposed: { class: "bg-purple-500/20 text-purple-400 border-purple-500/30", label: "Créneau alternatif proposé" },
      disputed: { class: "bg-orange-500/20 text-orange-400 border-orange-500/30", label: "Litige" }
    };
    const s = statusMap[status] || { class: "bg-gray-500/20 text-gray-400 border-gray-500/30", label: status };
    return <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${s.class}`}>{s.label}</span>;
  };

  const pendingRequests = requests.filter(r => r.status === "matched");
  const confirmedRequests = requests.filter(r => r.status === "confirmed" || r.status === "pending_payment");
  const activeRequests = requests.filter(r => ["alternative_proposed", "completed_by_nurse"].includes(r.status));
  const pastRequests = requests.filter(r => ["completed", "cancelled", "refused"].includes(r.status));
  
  // All completed interventions
  const completedInterventions = pastRequests.filter(r => r.status === "completed");

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0D1C32] flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-[#00E073] animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0D1C32]">
      <nav className="bg-[#1F2A38] border-b border-[#2A3A4D]">
        <div className="container-ip">
          <div className="flex items-center justify-between h-16">
            <Link to="/">
              <img src={LOGO_URL} alt="Infirmiers Privés" className="h-12 w-auto object-contain" />
            </Link>
            <UserMenu 
              userName={user?.name}
              showSettings={true}
              onSettings={() => navigate("/nurse/profile")}
            />
          </div>
        </div>
      </nav>

      <main className="container-ip py-8">
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-white">Tableau de bord</h1>
          <p className="text-[#94A3B8]">Bienvenue, {user?.name}</p>
        </div>

        {!profile?.is_active && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-4 mb-6 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-yellow-500 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-yellow-500 font-medium">Compte en attente de validation</p>
              <p className="text-sm text-[#94A3B8]">Vos documents sont en cours de vérification.</p>
            </div>
          </div>
        )}

        {/* Stats - Clickable */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div 
            className={`kpi-card cursor-pointer transition-all hover:border-amber-400/50 ${statusFilter === 'pending' ? 'border-amber-400 ring-2 ring-amber-400/30' : ''}`}
            onClick={() => {
              setActiveTab("current");
              setStatusFilter(statusFilter === 'pending' ? null : 'pending');
              requestsListRef.current?.scrollIntoView({ behavior: 'smooth' });
            }}
            data-testid="stat-pending"
          >
            <div className="kpi-value text-amber-400">{pendingRequests.length}</div>
            <div className="kpi-label">En attente</div>
          </div>
          <div 
            className={`kpi-card cursor-pointer transition-all hover:border-emerald-400/50 ${statusFilter === 'confirmed' ? 'border-emerald-400 ring-2 ring-emerald-400/30' : ''}`}
            onClick={() => {
              setActiveTab("current");
              setStatusFilter(statusFilter === 'confirmed' ? null : 'confirmed');
              requestsListRef.current?.scrollIntoView({ behavior: 'smooth' });
            }}
            data-testid="stat-confirmed"
          >
            <div className="kpi-value text-emerald-400">{confirmedRequests.length}</div>
            <div className="kpi-label">À réaliser</div>
          </div>
          <div className="kpi-card">
            <div className="kpi-value text-white">
              {requests.reduce((sum, r) => sum + (r.nurse_amount || 0), 0).toFixed(0)} €
            </div>
            <div className="kpi-label">Revenus totaux</div>
          </div>
          <div 
            className={`kpi-card cursor-pointer transition-all hover:border-[#28CAD4]/50 ${statusFilter === 'completed' ? 'border-[#28CAD4] ring-2 ring-[#28CAD4]/30' : ''}`}
            onClick={() => {
              setActiveTab("history");
              setStatusFilter(null);
              requestsListRef.current?.scrollIntoView({ behavior: 'smooth' });
            }}
            data-testid="stat-completed"
          >
            <div className="kpi-value text-[#28CAD4]">{completedInterventions.length}</div>
            <div className="kpi-label">Terminées</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto">
          <button
            onClick={() => setActiveTab("current")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === "current"
                ? "bg-[#00E073] text-[#0D1C32]"
                : "bg-[#1F2A38] text-[#94A3B8] hover:text-white"
            }`}
          >
            Interventions en cours
          </button>
          <button
            onClick={() => setActiveTab("history")}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${
              activeTab === "history"
                ? "bg-[#28CAD4] text-[#0D1C32]"
                : "bg-[#1F2A38] text-[#94A3B8] hover:text-white"
            }`}
          >
            <FileText className="w-4 h-4 inline mr-2" />
            Historique ({completedInterventions.length})
          </button>
          {disputes.length > 0 && (
            <button
              onClick={() => setActiveTab("disputes")}
              data-testid="disputes-tab-btn"
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap flex items-center gap-2 ${
                activeTab === "disputes"
                  ? "bg-orange-500 text-white"
                  : "bg-[#1F2A38] text-orange-300 hover:text-white"
              }`}
            >
              <AlertCircle className="w-4 h-4" />
              Litiges ({disputes.filter(d => d.status === "open").length})
            </button>
          )}
          <button
            onClick={() => setShowCalendar(true)}
            className="px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap bg-[#1F2A38] text-[#94A3B8] hover:text-white hover:bg-[#2A3A4D] ml-auto"
            data-testid="open-calendar-btn"
          >
            <CalendarDays className="w-4 h-4 inline mr-2" />
            Mon calendrier
          </button>
        </div>

        {activeTab === "current" && (
          <>
        {/* Pending Nurse Validation */}
        {pendingRequests.length > 0 && (
          <CollapsibleCard
            title={`Nouvelles demandes à accepter (${pendingRequests.length})`}
            icon={AlertCircle}
            iconClass="text-amber-400"
            borderClass="border-amber-500/30"
            defaultOpen
            testId="toggle-pending-section"
          >
            <div className="space-y-4">
              {pendingRequests.map((req) => (
                <div key={req.request_id} className="bg-[#0D1C32] rounded-xl p-4 border border-amber-500/20" data-testid={`pending-request-${req.request_id}`}>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="font-semibold text-white">{req.care_type || "Demande de soin"}</h3>
                          {req.is_urgent && (
                            <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30">
                              <AlertCircle className="w-3 h-3 mr-1" />
                              Urgent
                            </Badge>
                          )}
                          {getStatusBadge(req.status)}
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-[#94A3B8]">
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="w-4 h-4" />
                            {req.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {req.time}
                            {formatDuration(req.duration_minutes) && (
                              <span className="text-[#00E073] ml-1" data-testid={`duration-${req.request_id}`}>· {formatDuration(req.duration_minutes)}</span>
                            )}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {req.city} ({req.postal_code})
                          </span>
                        </div>
                        {req.address && (
                          <p className="text-sm text-[#64748B] mt-1">{req.address}</p>
                        )}

                        {/* Patient contact - to coordinate the intervention outside the app */}
                        <div className="mt-3 p-3 bg-[#1F2A38] rounded-lg border border-[#2A3A4D]" data-testid={`patient-contact-${req.request_id}`}>
                          <p className="text-xs text-[#94A3B8] mb-2 flex items-center gap-1">
                            <User className="w-3 h-3" />
                            Coordonnées du patient (pour convenir des soins)
                          </p>
                          {req.patient_name && (
                            <p className="text-sm text-white font-medium">{req.patient_name}</p>
                          )}
                          {req.patient_nationality && (
                            <p className="text-xs text-[#94A3B8] flex items-center gap-1 mt-0.5" data-testid={`patient-nationality-${req.request_id}`}>
                              <Flag className="w-3 h-3 text-[#00E073]" />
                              Nationalité : <span className="text-white">{req.patient_nationality}</span>
                            </p>
                          )}
                          <div className="flex flex-col sm:flex-row sm:gap-4 mt-1">
                            {req.patient_phone && (
                              <a href={`tel:${req.patient_phone}`} className="text-sm text-[#00E073] hover:underline flex items-center gap-1" data-testid={`patient-phone-${req.request_id}`}>
                                <Phone className="w-3.5 h-3.5" />
                                {req.patient_phone}
                              </a>
                            )}
                            {req.patient_email && (
                              <a href={`mailto:${req.patient_email}`} className="text-sm text-[#00E073] hover:underline flex items-center gap-1" data-testid={`patient-email-${req.request_id}`}>
                                <Mail className="w-3.5 h-3.5" />
                                {req.patient_email}
                              </a>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-[#00E073]">{req.nurse_amount?.toFixed(2)} €</p>
                        <p className="text-xs text-[#64748B]">Vos honoraires</p>
                        {req.is_urgent && req.urgent_fee > 0 && (
                          <p className="text-xs text-amber-400 mt-1">
                            (dont {req.urgent_fee}€ supplément urgent)
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Action Buttons */}
                    {showProposalForm !== req.request_id ? (
                      <div className="flex flex-wrap gap-3 pt-2 border-t border-[#2A3A4D]">
                        <Button
                          className="bg-emerald-600 hover:bg-emerald-700 text-white"
                          onClick={() => { setAcceptDuration(30); setAcceptModal({ requestId: req.request_id }); }}
                          disabled={responding === req.request_id}
                          data-testid={`accept-btn-${req.request_id}`}
                        >
                          {responding === req.request_id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                          Accepter
                        </Button>
                        <Button
                          variant="outline"
                          className="border-purple-500/50 text-purple-400 hover:bg-purple-500/10"
                          onClick={() => {
                            setShowProposalForm(req.request_id);
                            setProposalData({ date: null, time: "" });
                          }}
                          disabled={responding === req.request_id}
                          data-testid={`propose-btn-${req.request_id}`}
                        >
                          <RefreshCw className="w-4 h-4 mr-2" />
                          Proposer un autre créneau
                        </Button>
                        <Button
                          variant="outline"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          onClick={() => {
                            if (window.confirm("Êtes-vous sûr de vouloir refuser cette demande ?")) {
                              handleNurseRespond(req.request_id, "refuse");
                            }
                          }}
                          disabled={responding === req.request_id}
                          data-testid={`refuse-btn-${req.request_id}`}
                        >
                          <X className="w-4 h-4 mr-2" />
                          Refuser
                        </Button>
                      </div>
                    ) : (
                      /* Proposal Form */
                      <div className="pt-3 border-t border-[#2A3A4D] space-y-4">
                        <p className="text-sm font-medium text-purple-400">Proposer un créneau alternatif</p>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <div>
                            <Label className="form-label">Nouvelle date</Label>
                            <Popover>
                              <PopoverTrigger asChild>
                                <Button variant="outline" className="w-full justify-start text-left font-normal input-dark">
                                  <CalendarIcon className="mr-2 h-4 w-4" />
                                  {proposalData.date ? format(proposalData.date, "dd/MM/yyyy") : "Choisir une date"}
                                </Button>
                              </PopoverTrigger>
                              <PopoverContent className="w-auto p-0 bg-[#1F2A38] border-[#2A3A4D]" align="start">
                                <Calendar
                                  mode="single"
                                  selected={proposalData.date}
                                  onSelect={(d) => setProposalData(prev => ({ ...prev, date: d }))}
                                  locale={fr}
                                  disabled={(date) => date < new Date()}
                                  className="text-white"
                                />
                              </PopoverContent>
                            </Popover>
                          </div>
                          <div>
                            <Label className="form-label">Nouvel horaire</Label>
                            <Select value={proposalData.time} onValueChange={(v) => setProposalData(prev => ({ ...prev, time: v }))}>
                              <SelectTrigger className="input-dark">
                                <SelectValue placeholder="Choisir un horaire" />
                              </SelectTrigger>
                              <SelectContent className="bg-[#1F2A38] border-[#2A3A4D]">
                                {TIME_SLOTS.map(t => (
                                  <SelectItem key={t} value={t} className="text-white hover:bg-[#2A3A4D]">{t}</SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <div className="flex gap-3">
                          <Button
                            className="bg-purple-600 hover:bg-purple-700 text-white"
                            onClick={() => handleProposeAlternative(req.request_id)}
                            disabled={responding === req.request_id}
                            data-testid={`submit-proposal-${req.request_id}`}
                          >
                            {responding === req.request_id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                            Envoyer la proposition
                          </Button>
                          <Button
                            variant="outline"
                            className="btn-outline"
                            onClick={() => setShowProposalForm(null)}
                          >
                            Annuler
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleCard>
        )}

        {/* Confirmed Interventions - Ready to be completed */}
        {confirmedRequests.length > 0 && (
          <CollapsibleCard
            title={`Interventions à réaliser (${confirmedRequests.length})`}
            icon={CheckCircle}
            iconClass="text-emerald-400"
            borderClass="border-emerald-500/30"
            defaultOpen
            testId="toggle-confirmed-section"
          >
            <div className="space-y-4">
              {/* Regular care requests */}
              {confirmedRequests.map((req) => (
                <div key={req.request_id} className={`bg-[#0D1C32] rounded-xl p-4 border ${req.status === "pending_payment" ? "border-amber-500/30" : "border-emerald-500/20"}`} data-testid={`confirmed-request-${req.request_id}`}>
                  <div className="flex flex-col gap-4">
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                      <div>
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="font-semibold text-white">{req.care_type || "Demande de soin"}</h3>
                          {req.status === "pending_payment" ? (
                            <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30">
                              En attente de paiement du patient
                            </Badge>
                          ) : (
                            getStatusBadge(req.status)
                          )}
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-[#94A3B8]">
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="w-4 h-4" />
                            {req.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {req.time}
                            {formatDuration(req.duration_minutes) && (
                              <span className="text-[#00E073] ml-1" data-testid={`duration-${req.request_id}`}>· {formatDuration(req.duration_minutes)}</span>
                            )}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {req.city} ({req.postal_code})
                          </span>
                        </div>
                        {req.address && (
                          <p className="text-sm text-[#64748B] mt-1">{req.address}</p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-[#00E073]">{req.nurse_amount?.toFixed(2)} €</p>
                        <p className="text-xs text-[#64748B]">Vos honoraires</p>
                      </div>
                    </div>

                    {req.status === "pending_payment" ? (
                      <div className="pt-2 border-t border-[#2A3A4D]">
                        <p className="text-sm text-amber-400 flex items-center gap-2" data-testid={`awaiting-payment-${req.request_id}`}>
                          <Clock className="w-4 h-4" />
                          En attente du paiement du patient. L'intervention sera confirmée une fois le règlement effectué.
                        </p>
                        {(req.patient_phone || req.patient_email) && (
                          <p className="text-xs text-[#64748B] mt-2">
                            Relancer le patient : {req.patient_name}
                            {req.patient_phone && <> — <a href={`tel:${req.patient_phone}`} className="text-[#00E073] hover:underline">{req.patient_phone}</a></>}
                            {req.patient_email && <> — <a href={`mailto:${req.patient_email}`} className="text-[#00E073] hover:underline">{req.patient_email}</a></>}
                          </p>
                        )}
                      </div>
                    ) : (
                      /* Actions once paid (confirmed) */
                      <div className="pt-2 border-t border-[#2A3A4D] flex flex-wrap gap-2">
                        <Button
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          onClick={() => handleMarkCompleted(req.request_id)}
                          disabled={markingCompleted === req.request_id}
                          data-testid={`mark-completed-btn-${req.request_id}`}
                        >
                          {markingCompleted === req.request_id ? (
                            <Loader2 className="w-4 h-4 animate-spin mr-2" />
                          ) : (
                            <Check className="w-4 h-4 mr-2" />
                          )}
                          Marquer comme réalisé
                        </Button>
                        <Button
                          variant="outline"
                          className="border-amber-500/40 text-amber-400 hover:bg-amber-500/10"
                          onClick={() => setCannotCompleteModal({ requestId: req.request_id, step: "choice", comment: "" })}
                          data-testid={`cannot-complete-btn-${req.request_id}`}
                        >
                          <XCircle className="w-4 h-4 mr-2" />
                          La prestation n'a pas pu avoir lieu
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </CollapsibleCard>
        )}
          </>
        )}

        {activeTab === "history" && (
          /* History Tab */
          <div className="card-dark p-6">
            <h2 className="text-lg font-semibold text-white mb-4">
              Interventions terminées ({completedInterventions.length})
            </h2>
            
            {completedInterventions.length === 0 ? (
              <div className="empty-state">
                <CheckCircle className="empty-state-icon" />
                <p className="empty-state-text">Aucune intervention terminée pour le moment</p>
              </div>
            ) : (
              <div className="space-y-4">
                {completedInterventions.map((req) => (
                  <div key={req.request_id} className="bg-[#0D1C32] rounded-xl p-4 border border-green-500/20">
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
                      <div>
                        <div className="flex items-center gap-2 mb-2 flex-wrap">
                          <h3 className="font-semibold text-white">{req.care_type || "Demande de soin"}</h3>
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border bg-green-500/20 text-green-400 border-green-500/30">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Terminé et validé
                          </span>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-[#94A3B8]">
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="w-4 h-4" />
                            {req.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {req.time}
                            {formatDuration(req.duration_minutes) && (
                              <span className="text-[#00E073] ml-1" data-testid={`duration-${req.request_id}`}>· {formatDuration(req.duration_minutes)}</span>
                            )}
                          </span>
                          <span className="flex items-center gap-1">
                            <MapPin className="w-4 h-4" />
                            {req.city} ({req.postal_code})
                          </span>
                        </div>
                        {req.validated_at && (
                          <p className="text-xs text-[#64748B] mt-2">
                            Validé le {new Date(req.validated_at).toLocaleDateString('fr-FR')}
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-lg font-semibold text-[#00E073]">{req.nurse_amount?.toFixed(2)} €</p>
                        <p className="text-xs text-[#64748B]">Vos honoraires</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "disputes" && (
          <div className="card-dark p-6">
            <h2 className="text-lg font-semibold text-white mb-2 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-orange-400" />
              Litiges déclarés contre vous
            </h2>
            <p className="text-sm text-[#94A3B8] mb-4">
              Le patient a déclaré que l'intervention n'avait pas été réalisée. Vous pouvez répondre une fois pour donner votre version avant la décision de l'équipe d'administration.
            </p>
            {disputes.length === 0 ? (
              <p className="text-sm text-[#64748B]">Aucun litige.</p>
            ) : (
              <div className="space-y-4">
                {disputes.map((d) => {
                  const intervention = d.intervention || {};
                  const statusLabel = d.status === "open" ? "Ouvert" : d.status === "resolved" ? "Résolu" : "Rejeté";
                  const statusClass = d.status === "open" ? "bg-orange-500/20 text-orange-300" : d.status === "resolved" ? "bg-emerald-500/20 text-emerald-300" : "bg-red-500/20 text-red-300";
                  return (
                    <div key={d.dispute_id} className="bg-[#0D1C32] rounded-xl p-4 border border-[#2A3A4D]" data-testid={`nurse-dispute-card-${d.dispute_id}`}>
                      <div className="flex flex-wrap items-center gap-2 mb-3">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusClass}`}>
                          {statusLabel}
                        </span>
                        <span className="text-xs text-[#64748B] ml-auto">
                          Reçu le {new Date(d.created_at).toLocaleString("fr-FR")}
                        </span>
                      </div>

                      <div className="bg-[#1F2A38] rounded-lg p-3 mb-3">
                        <p className="text-xs text-[#64748B] mb-1 uppercase tracking-wide">Intervention concernée</p>
                        <div className="text-sm text-[#E2E8F0] space-y-1">
                          {intervention.care_type && <div>Soin : <span className="text-white">{intervention.care_type}</span></div>}
                          {intervention.date && <div>Date : <span className="text-white">{intervention.date}{intervention.time ? ` à ${intervention.time}` : ""}</span></div>}
                          {intervention.start_date && <div>Période : <span className="text-white">{intervention.start_date} → {intervention.end_date}</span></div>}
                          {(intervention.address || intervention.city) && <div>Adresse : <span className="text-white">{[intervention.address, intervention.city].filter(Boolean).join(", ")}</span></div>}
                        </div>
                      </div>

                      <div className="bg-[#1F2A38] rounded-lg p-3 mb-3 border-l-4 border-orange-400">
                        <p className="text-xs text-[#64748B] mb-1 uppercase tracking-wide">Motif déclaré par le patient</p>
                        <p className="text-sm text-[#E2E8F0] whitespace-pre-wrap">{d.description}</p>
                      </div>

                      {d.nurse_response ? (
                        <div className="bg-[#1F2A38] rounded-lg p-3 border-l-4 border-emerald-400" data-testid={`nurse-response-${d.dispute_id}`}>
                          <p className="text-xs text-[#64748B] mb-1 uppercase tracking-wide">Votre réponse {d.nurse_response_at ? `(envoyée le ${new Date(d.nurse_response_at).toLocaleString("fr-FR")})` : ""}</p>
                          <p className="text-sm text-[#E2E8F0] whitespace-pre-wrap">{d.nurse_response}</p>
                          <p className="text-xs text-[#94A3B8] mt-2">En attente de la décision de l'équipe d'administration.</p>
                        </div>
                      ) : d.status !== "open" ? (
                        <p className="text-sm text-[#94A3B8]">Ce litige a été clôturé sans réponse de votre part.</p>
                      ) : (
                        <div className="space-y-2">
                          <label className="text-sm text-[#94A3B8] block">Votre réponse (20 caractères minimum) :</label>
                          <textarea
                            value={disputeResponses[d.dispute_id] || ""}
                            onChange={(e) => setDisputeResponses(prev => ({ ...prev, [d.dispute_id]: e.target.value }))}
                            placeholder="Expliquez votre version : preuves de présence, horaires, échanges avec le patient..."
                            className="w-full bg-[#0D1C32] border border-[#2A3A4D] rounded-lg p-3 text-white text-sm resize-none focus:outline-none focus:border-emerald-400"
                            rows={4}
                            data-testid={`nurse-dispute-response-input-${d.dispute_id}`}
                          />
                          <div className="flex items-center justify-between">
                            <p className={`text-xs ${(disputeResponses[d.dispute_id] || "").trim().length >= 20 ? "text-[#64748B]" : "text-orange-400"}`}>
                              {(disputeResponses[d.dispute_id] || "").trim().length} / 20 caractères
                            </p>
                            <Button
                              size="sm"
                              className="bg-emerald-600 hover:bg-emerald-700 text-white"
                              onClick={() => handleSubmitDisputeResponse(d.dispute_id)}
                              disabled={submittingDisputeResponse === d.dispute_id || (disputeResponses[d.dispute_id] || "").trim().length < 20}
                              data-testid={`submit-nurse-dispute-response-${d.dispute_id}`}
                            >
                              {submittingDisputeResponse === d.dispute_id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                              Envoyer ma réponse
                            </Button>
                          </div>
                          <p className="text-xs text-[#64748B]">Note : vous ne pourrez répondre qu'une seule fois. Soyez précis.</p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === "current" && (
          <>
        <div className="grid lg:grid-cols-3 gap-6 mt-6">
          {/* Active & Past Interventions */}
          <div className="lg:col-span-2">
            <CollapsibleCard title="Mes interventions" defaultOpen={false} testId="toggle-my-interventions">
              {[...activeRequests, ...pastRequests].length === 0 ? (
                <div className="empty-state">
                  <CalendarIcon className="empty-state-icon" />
                  <p className="empty-state-text">Aucune intervention pour le moment</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {[...activeRequests, ...pastRequests].map((req) => (
                    <div key={req.request_id} className="bg-[#0D1C32] rounded-xl p-4 border border-[#2A3A4D]">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <div className="flex items-center gap-2 mb-2">
                            <h3 className="font-medium text-white">{req.care_type}</h3>
                            {getStatusBadge(req.status)}
                          </div>
                          <div className="flex flex-wrap gap-4 text-sm text-[#94A3B8]">
                            <span className="flex items-center gap-1">
                              <CalendarIcon className="w-4 h-4" />
                              {req.date}
                            </span>
                            <span className="flex items-center gap-1">
                              <Clock className="w-4 h-4" />
                              {req.time}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="w-4 h-4" />
                              {req.city}
                            </span>
                          </div>
                          {req.status === "alternative_proposed" && (
                            <p className="text-sm text-purple-400 mt-2">
                              Créneau proposé : {req.proposed_date} à {req.proposed_time}
                            </p>
                          )}
                        </div>
                        <div className="text-right">
                          <p className="text-lg font-semibold text-[#00E073]">{req.nurse_amount?.toFixed(2)} €</p>
                          <p className="text-xs text-[#64748B]">Vos honoraires</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CollapsibleCard>
          </div>

          {/* Profile Summary */}
          <div className="space-y-6">
            <CollapsibleCard title="Mon profil" defaultOpen={false} testId="toggle-my-profile">
              <div className="space-y-4">
                <div>
                  <p className="text-sm text-[#94A3B8] mb-1">Zones d'intervention</p>
                  <div className="flex flex-wrap gap-2">
                    {profile?.zones?.length > 0 ? (
                      profile.zones.map((zone, i) => (
                        <Badge key={typeof zone === 'string' ? `${zone}-${i}` : `${zone.city}-${i}`} variant="secondary" className="bg-[#2A3A4D] text-white">
                          {typeof zone === 'string' ? zone : `${zone.city} (${zone.radius} km)`}
                        </Badge>
                      ))
                    ) : (
                      <span className="text-[#64748B]">Non configuré</span>
                    )}
                  </div>
                </div>
                <div>
                  <p className="text-sm text-[#94A3B8] mb-1">Tarif de base</p>
                  <p className="text-xl font-semibold text-white">{profile?.base_rate} €</p>
                </div>
                <Link to="/nurse/profile">
                  <Button className="btn-secondary w-full mt-4" data-testid="edit-profile-btn">
                    <Settings className="w-4 h-4 mr-2" />
                    Modifier mon profil
                  </Button>
                </Link>
              </div>
            </CollapsibleCard>

            <div className="card-dark p-6">
              <div className="flex items-center gap-2 mb-3">
                <AlertCircle className="w-5 h-5 text-[#28CAD4]" />
                <span className="font-semibold text-white">Besoin d'aide ?</span>
              </div>
              <p className="text-sm text-[#94A3B8] mb-4">
                Notre équipe vous accompagne dans le développement de votre activité.
              </p>
              <Button variant="outline" className="btn-outline w-full text-sm">
                Contacter le support
              </Button>
            </div>
          </div>
        </div>
          </>
        )}

        <div className="legal-mention mt-8">
          <p>
            <strong>Rappel :</strong> En tant qu'infirmier libéral, vous exercez en toute indépendance. 
            Infirmiers Privés est une plateforme d'intermédiation et perçoit uniquement des frais de service (15%), auxquels s'ajoute la TVA (20%).
          </p>
        </div>
      </main>
      
      {/* Nurse Calendar Modal */}
      {showCalendar && (
        <NurseCalendar onClose={() => setShowCalendar(false)} />
      )}

      {/* Accept + estimated duration Modal */}
      {acceptModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="accept-modal">
          <div className="bg-[#1F2A38] rounded-xl p-6 max-w-md w-full border border-[#2A3A4D]">
            <div className="flex items-center gap-2 mb-2">
              <Check className="w-5 h-5 text-emerald-400" />
              <h3 className="text-lg font-semibold text-white">Accepter la demande</h3>
            </div>
            <p className="text-sm text-[#94A3B8] mb-5">
              Estimez le temps de l'intervention. Les créneaux suivants seront bloqués pour éviter une double réservation.
            </p>

            <Label className="form-label flex items-center gap-2 mb-2">
              <Clock className="w-4 h-4" /> Durée estimée
            </Label>
            <div className="grid grid-cols-4 gap-2 mb-6">
              {DURATION_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  onClick={() => setAcceptDuration(opt.value)}
                  className={`py-2 rounded-lg text-sm font-medium transition-all border ${
                    acceptDuration === opt.value
                      ? "bg-[#00E073] text-[#0D1C32] border-[#00E073]"
                      : "bg-[#0D1C32] text-white border-[#2A3A4D] hover:border-[#00E073]/50"
                  }`}
                  data-testid={`accept-duration-${opt.value}`}
                >
                  {opt.label}
                </button>
              ))}
            </div>

            <div className="flex gap-3">
              <Button
                variant="ghost"
                className="flex-1 text-[#94A3B8] hover:text-white"
                onClick={() => setAcceptModal(null)}
                disabled={responding === acceptModal.requestId}
                data-testid="accept-cancel-btn"
              >
                Annuler
              </Button>
              <Button
                className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => handleNurseRespond(acceptModal.requestId, "accept", { duration_minutes: acceptDuration })}
                disabled={responding === acceptModal.requestId}
                data-testid="accept-confirm-btn"
              >
                {responding === acceptModal.requestId ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Check className="w-4 h-4 mr-2" />}
                Confirmer l'acceptation
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* "Prestation non réalisée" Modal */}
      {cannotCompleteModal && (
        <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4" data-testid="cannot-complete-modal">
          <div className="bg-[#1F2A38] rounded-xl p-6 max-w-md w-full border border-[#2A3A4D]">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="w-5 h-5 text-amber-400" />
              <h3 className="text-lg font-semibold text-white">La prestation n'a pas pu avoir lieu</h3>
            </div>
            <p className="text-sm text-[#94A3B8] mb-5">
              Que souhaitez-vous faire ? Le patient sera informé par email dans les deux cas.
            </p>

            <div className="space-y-3">
              <div className="bg-[#0D1C32] rounded-lg p-4 border border-[#2A3A4D]">
                <p className="text-white font-medium mb-1">Déclarer un litige</p>
                <p className="text-xs text-[#94A3B8] mb-3">
                  Le paiement reste séquestré et l'administration tranchera. Expliquez ce qui s'est passé (un récapitulatif sera envoyé au patient).
                </p>
                <Textarea
                  value={cannotCompleteModal.comment}
                  onChange={(e) => setCannotCompleteModal({ ...cannotCompleteModal, comment: e.target.value })}
                  placeholder="Motif détaillé (20 caractères minimum)..."
                  className="bg-[#0D1C32] border-[#2A3A4D] text-white mb-3"
                  rows={3}
                  data-testid="cannot-complete-comment"
                />
                <Button
                  className="w-full bg-amber-500 hover:bg-amber-600 text-[#0D1C32] font-medium"
                  onClick={() => handleCannotComplete("dispute")}
                  disabled={submittingCannot}
                  data-testid="cannot-complete-dispute-btn"
                >
                  {submittingCannot ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <AlertTriangle className="w-4 h-4 mr-2" />}
                  Déclarer un litige
                </Button>
              </div>

              <div className="bg-[#0D1C32] rounded-lg p-4 border border-[#2A3A4D]">
                <p className="text-white font-medium mb-1">Rembourser le patient</p>
                <p className="text-xs text-[#94A3B8] mb-3">
                  Remboursement intégral immédiat. Le patient reçoit un email d'information.
                </p>
                <Button
                  className="w-full btn-primary"
                  onClick={() => handleCannotComplete("refund")}
                  disabled={submittingCannot}
                  data-testid="cannot-complete-refund-btn"
                >
                  {submittingCannot ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                  Rembourser le patient
                </Button>
              </div>
            </div>

            <Button
              variant="ghost"
              className="w-full mt-4 text-[#94A3B8] hover:text-white"
              onClick={() => setCannotCompleteModal(null)}
              disabled={submittingCannot}
              data-testid="cannot-complete-cancel-btn"
            >
              Annuler
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default NurseDashboard;
